/* eslint-disable jsx-a11y/accessible-emoji */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { useScreenVoice } from "./useScreenVoice";
import { SAFETY_QUESTIONS } from "./GSA_CONSTANTS";
import { MYRA_MESSAGES } from "./GSA_MYRA_MESSAGES";
import styles from "./SelfAssessment.module.css";

let _ssId = 80000;
function nextSsId() {
  return ++_ssId;
}

export default function SafetyScreeningScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue, onHardStop }) {
  const problemArea = assessmentData.problemArea;
  const safetyData = SAFETY_QUESTIONS[problemArea];

  const [checkedItems, setCheckedItems] = useState(new Set());
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);

  // ── Screen status ──
  // 'checklist' → user is selecting conditions
  // 'waiting'   → conditions submitted, Claude is conducting follow-up conversation
  // 'advisory'  → SAFETY_ADVISORY received, user chooses Continue or Leave
  // 'stopped'   → SAFETY_STOP received or user chose Leave on advisory
  const [screenStatus, setScreenStatus] = useState("checklist");
  const [outcomeText, setOutcomeText] = useState("");
  const screenStatusRef = useRef("checklist");
  const checkedItemsRef = useRef([]);

  // Keep ref in sync for use inside command handlers
  useEffect(() => {
    screenStatusRef.current = screenStatus;
  }, [screenStatus]);

  // ── Push divider + Myra message on mount ──
  useEffect(() => {
    if (!safetyData) return;
    let cancelled = false;
    async function init() {
      pushToHistory({ type: "divider", id: nextSsId() });
      await showMyraMessage(safetyData.prompt, 900);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Voice boilerplate ──
  // useScreenVoice({
  //   screenName: "SafetyScreeningScreen",
  //   trackerName: "safety_screening",
  //   flowName: "flow_gsa",
  //   payloadKey: "SafetyScreeningScreen",
  //   skipFlow: true,
  // });
  useScreenVoice({
    screenName: "SafetyScreeningScreen",
    trackerName: "safety_screening",
    flowName: "flow_gsa",
    payloadKey: "SafetyScreeningScreen",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value) => {
        if (value === "continue_command") handleSubmit();
      },
      MOVE_ON: () => handleSubmit(),
      SAFETY_CONTINUE: (value) => {
        alert("1");
        // gtag conversion
        if (window.gtag) {
          window.gtag("event", "conversion", {
            send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
            value: 1.0,
            currency: "USD",
          });
        }
        onContinue({
          safetyScreenResult: "CLEAR",
          safetyScreenSelection: checkedItemsRef.current,
          safetyOutcome: "CONTINUE",
        });
      },

      SAFETY_STOP: (value) => {
        alert("2");
        setOutcomeText(value || "");
        setScreenStatus("stopped");
        screenStatusRef.current = "stopped";

        // Push stop card to conversation history
        pushToHistory({
          type: "frozen-widget",
          widgetType: "safety_stop_card",
          id: nextSsId(),
          data: { detail: value || "" },
        });

        // Follow-up message — Myra stays available
        const followUp = MYRA_MESSAGES.safetyOutcome?.stoppedFollowUp || /* PLACEHOLDER COPY */ "I know this isn't the news you were hoping for. If you have any questions, I'm right here.";
        showMyraMessage(followUp, 800);
      },

      SAFETY_ADVISORY: (value) => {
        alert("3");
        setOutcomeText(value || "");
        setScreenStatus("advisory");
        screenStatusRef.current = "advisory";
      },
    },
    exitReason: "safety_screening_completed",
  });
  // ── Command registration ──
  useEffect(() => {
    window.currentCommandsAudio = {
      // Voice/chat "continue" during checklist phase only
      ANSWER_MidJourneyFlow_QUESTION: (value) => {
        if (window.exitHandlerOpen) return;
        if (screenStatusRef.current !== "checklist") return;
        if (value === "continue_command") handleSubmitRef.current();
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (screenStatusRef.current !== "checklist") return;
        handleSubmitRef.current();
      },

      // ── Safety outcome actions from backend ──

      SAFETY_CONTINUE: (value) => {
        alert("1");
        // gtag conversion
        if (window.gtag) {
          window.gtag("event", "conversion", {
            send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
            value: 1.0,
            currency: "USD",
          });
        }
        onContinue({
          safetyScreenResult: "CLEAR",
          safetyScreenSelection: checkedItemsRef.current,
          safetyOutcome: "CONTINUE",
        });
      },

      SAFETY_STOP: (value) => {
        alert("2");
        setOutcomeText(value || "");
        setScreenStatus("stopped");
        screenStatusRef.current = "stopped";

        // Push stop card to conversation history
        pushToHistory({
          type: "frozen-widget",
          widgetType: "safety_stop_card",
          id: nextSsId(),
          data: { detail: value || "" },
        });

        // Follow-up message — Myra stays available
        const followUp = MYRA_MESSAGES.safetyOutcome?.stoppedFollowUp || /* PLACEHOLDER COPY */ "I know this isn't the news you were hoping for. If you have any questions, I'm right here.";
        showMyraMessage(followUp, 800);
      },

      SAFETY_ADVISORY: (value) => {
        alert("3");
        setOutcomeText(value || "");
        setScreenStatus("advisory");
        screenStatusRef.current = "advisory";
      },
    };
  }, []);

  // ── Stable ref for handleSubmit (avoids stale closure in commands) ──
  const handleSubmitRef = useRef(null);

  // ── Toggle a safety item ──
  const toggleItem = useCallback(
    (id) => {
      if (locked) return;
      setCheckedItems((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [locked],
  );

  // ── Submit checklist ──
  const handleSubmit = useCallback(() => {
    if (locked) return;
    setLocked(true);

    const checked = [...checkedItems];

    // No items checked → clear, continue immediately
    if (checked.length === 0) {
      pushToHistory({ type: "frozen-widget", widgetType: "safety_checklist", id: nextSsId(), data: { result: "none", labels: "" } }, { type: "user", text: "None of these apply", id: nextSsId() });

      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
          value: 1.0,
          currency: "USD",
        });
      }
      onContinue({
        safetyScreenResult: "CLEAR",
        safetyScreenSelection: [],
        safetyOutcome: "CONTINUE",
      });
      return;
    }

    // Build labels for user echo
    const labels = checked.map((id) => safetyData.conditions.find((c) => c.id === id)?.label).filter(Boolean);

    // Push frozen widget + user echo
    pushToHistory({ type: "frozen-widget", widgetType: "safety_checklist", id: nextSsId(), data: { result: "checked", labels: labels.join(", ") } }, { type: "user", text: labels.join(", "), id: nextSsId() });

    // Store checked items for use in command handlers
    checkedItemsRef.current = checked;

    // Enter waiting state — Claude conducts conversational follow-up.
    // The screen stays mounted. Intermediate conversation turns pass through
    // as ANSWER_MidJourneyFlow_QUESTION with intent "safety_screen_conversation"
    // and require no screen-level action. The final SAFETY_* action triggers
    // the appropriate handler above.
    setScreenStatus("waiting");
    screenStatusRef.current = "waiting";
  }, [checkedItems, locked, safetyData, onContinue, pushToHistory]);

  // Keep handleSubmitRef current
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // ── Advisory: user chooses Continue ──
  const handleAdvisoryContinue = useCallback(() => {
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
        value: 1.0,
        currency: "USD",
      });
    }
    onContinue({
      safetyScreenResult: "CLEAR",
      safetyScreenSelection: checkedItemsRef.current,
      safetyOutcome: "ADVISORY_CONTINUE",
    });
  }, [onContinue]);

  // ── Advisory: user chooses Leave ──
  const handleAdvisoryLeave = useCallback(() => {
    setScreenStatus("stopped");
    screenStatusRef.current = "stopped";

    // Push frozen card so the advisory text stays in history
    pushToHistory({
      type: "frozen-widget",
      widgetType: "safety_advisory_card",
      id: nextSsId(),
      data: { detail: outcomeText, choice: "leave" },
    });

    const leaveMsg = MYRA_MESSAGES.safetyOutcome?.advisoryLeave || /* PLACEHOLDER COPY */ "No problem at all. When you're ready to give it a try, I'll be right here.";
    showMyraMessage(leaveMsg, 800);
  }, [outcomeText, pushToHistory, showMyraMessage]);

  // ── No safety data fallback ──
  useEffect(() => {
    if (!safetyData) {
      onContinue({ safetyScreenResult: "CLEAR", safetyScreenSelection: [], safetyOutcome: "CONTINUE" });
    }
  }, [safetyData, onContinue]);

  if (!safetyData) return null;
  if (!ready) return null;

  // ── Waiting: no live UI needed, conversation happening via voice/chat ──
  if (screenStatus === "waiting") return null;

  // ── Stopped: no live UI needed, stop card + follow-up are in history ──
  if (screenStatus === "stopped") return null;

  // ── Advisory: show card with Continue / Leave ──
  if (screenStatus === "advisory") {
    return (
      <div className={styles.inlineUi}>
        <div
          style={{
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            borderRadius: "0.75rem",
            padding: "1.25rem",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>⚠️</div>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "#1f2937",
              marginBottom: "0.75rem",
            }}
          >
            {MYRA_MESSAGES.safetyOutcome?.advisoryHeading || /* PLACEHOLDER COPY */ "Something to consider before continuing"}
          </div>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#4b5563",
              lineHeight: 1.5,
              marginBottom: "1.25rem",
            }}
          >
            {outcomeText}
          </p>

          {/* Continue — primary action */}
          <button
            style={{
              width: "100%",
              background: "#0d9488",
              color: "white",
              fontWeight: "bold",
              fontSize: "16px",
              padding: "0.875rem",
              borderRadius: "0.5rem",
              border: "none",
              cursor: "pointer",
              marginBottom: "0.625rem",
            }}
            onClick={handleAdvisoryContinue}
          >
            Continue with assessment
          </button>

          {/* Leave — secondary action */}
          <button
            style={{
              width: "100%",
              background: "transparent",
              color: "#6b7280",
              fontWeight: 600,
              fontSize: "15px",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
            }}
            onClick={handleAdvisoryLeave}
          >
            I'll come back later
          </button>
        </div>
      </div>
    );
  }

  // ── Checklist phase ──
  const checkSvg = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  return (
    <div className={styles.inlineUi}>
      <div className={styles.safetyChecklist}>
        {safetyData.conditions.map((c) => {
          const isChecked = checkedItems.has(c.id);
          return (
            <div key={c.id} className={`${styles.safetyItem} ${isChecked ? styles.checked : ""} ${locked ? styles.locked : ""}`} onClick={() => toggleItem(c.id)}>
              <div className={styles.safetyCheckbox}>
                <span className={styles.safetyCheckboxCheck}>{checkSvg}</span>
              </div>
              <span className={styles.safetyLabel}>{c.label}</span>
            </div>
          );
        })}
      </div>

      <p className={styles.safetyHelper}>If none of these apply to you, just tap Continue</p>

      <button className={styles.safetyContinue} onClick={handleSubmit} disabled={locked}>
        Continue
      </button>
    </div>
  );
}
